import { useEffect, useState } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import type { Application } from '@cef-ai/wallet-api-client';
import { useWallet } from '~/wallet';
import { AppPage } from '~/ui/AppPage';
import { Card, Chip, CopyValue, ErrorText, Loading } from '~/ui/primitives';
import { capabilityLabel } from '~/ui/format';

export const ApplicationDetail = observer(() => {
  const { identity, applicationsStore } = useWallet();
  const { appId } = useParams<{ appId: string }>();
  const [app, setApp] = useState<Application | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!appId || !identity.isAuthenticated) return;
    applicationsStore
      .listAll()
      .then((apps) => apps.find((a) => a.appId === appId) ?? null)
      .then(setApp)
      .catch((e) => setError(e?.message ?? 'load failed'));
  }, [appId, applicationsStore, identity.isAuthenticated]);

  if (!identity.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppPage
      title={appId}
      actions={
        <Link to="/applications" style={{ font: '500 14px/1 var(--font-sans)', color: 'var(--text-secondary)' }}>
          ← Applications
        </Link>
      }
    >
      {error && <ErrorText>{error}</ErrorText>}
      {app === null && !error && (
        <Card>
          <Loading />
        </Card>
      )}
      {app && (
        <Card>
          <h2 style={{ margin: '0 0 var(--space-3)', font: '600 16px/1.2 var(--font-sans)' }}>Permissions</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: app.email ? 'var(--space-5)' : 0 }}>
            {Object.keys(app.permissions).map((key) => (
              <Chip key={key} tone="violet">
                {capabilityLabel(key)}
              </Chip>
            ))}
          </div>
          {app.email && <CopyValue label="Email" value={app.email} truncate={false} />}
        </Card>
      )}
    </AppPage>
  );
});
